import { IConsultationInvoice, IInvoiceItem } from '@Reducers/billing';
import { BillType } from '@Reducers/checkout';
import { IRx } from '@Reducers/encounter';
import { IPrice } from '@Reducers/patient';
import {
  CONSULTATION_NAMES,
  CONSULTATION_TYPE,
  ItemType,
  ONLINE_LOCATION_ID,
  PRACTO_LOCATION_ID,
} from '@Utils/constants';

const getItemPrices = (items: IInvoiceItem[]): IPrice => {
  if (!items?.length) return;
  return items.reduce(
    (acc, item) => ({
      listingPrice: acc.listingPrice + item.listingAmount,
      sellingPrice: acc.sellingPrice + item.payableAmount,
    }),
    { listingPrice: 0, sellingPrice: 0 },
  );
};

export const getItemPricesFuture = (items: IInvoiceItem[]): IPrice => {
  if (!items?.length) return;
  return items.reduce(
    (acc, item) => ({
      listingPrice: ((acc.listingPrice + item.listingAmount) / item.quantity) * item?.selection?.quantity,
      sellingPrice: ((acc.sellingPrice + item.payableAmount) / item.quantity) * item?.selection?.quantity,
    }),
    { listingPrice: 0, sellingPrice: 0 },
  );
};

const roundPrice = (price?: IPrice): IPrice =>
  price
    ? {
        listingPrice: Number.parseFloat(price.listingPrice.toFixed(2)),
        sellingPrice: Number.parseFloat(price.sellingPrice.toFixed(2)),
      }
    : undefined;

export const getInvoiceItemPrices = (invoiceItems: IInvoiceItem[]) => ({
  drugsPrice: roundPrice(getItemPrices(invoiceItems?.filter((item) => item.type === ItemType.DRUG))),
  labtestsPrice: roundPrice(getItemPrices(invoiceItems?.filter((item) => item.type === ItemType.LAB))),
  counsellingsPrice: roundPrice(
    getItemPrices(
      invoiceItems?.filter((item) => item.type === ItemType.CONSULTATION && item.name === CONSULTATION_NAMES.TH),
    ),
  ),
  followupPrice: roundPrice(
    getItemPrices(
      invoiceItems?.filter((item) => item.type === ItemType.CONSULTATION && item.name === CONSULTATION_NAMES.FU),
    ),
  ),
});

export const getInvoiceItemPricesFuture = (invoiceItems: IInvoiceItem[]) => ({
  drugsPrice: roundPrice(
    getItemPricesFuture(invoiceItems?.filter((item) => item.type === ItemType.DRUG && item?.selection?.isSelected)),
  ),
  labtestsPrice: roundPrice(
    getItemPricesFuture(invoiceItems?.filter((item) => item.type === ItemType.LAB && item?.selection?.isSelected)),
  ),
  counsellingsPrice: roundPrice(
    getItemPricesFuture(
      invoiceItems?.filter(
        (item) =>
          item.type === ItemType.CONSULTATION && item.name === CONSULTATION_NAMES.TH && item?.selection?.isSelected,
      ),
    ),
  ),
  followupPrice: roundPrice(
    getItemPricesFuture(
      invoiceItems?.filter(
        (item) =>
          item.type === ItemType.CONSULTATION && item.name === CONSULTATION_NAMES.FU && item?.selection?.isSelected,
      ),
    ),
  ),
});

export const getMandatoryItemPrices = (invoiceItems: IInvoiceItem[]) => ({
  drugsPrice: roundPrice(
    getItemPrices(
      invoiceItems?.filter((item) => item.type === ItemType.DRUG && !item.isOptional).length > 0
        ? invoiceItems?.filter((item) => item.type === ItemType.DRUG && !item.isOptional)
        : invoiceItems?.filter((item) => item.type === ItemType.DRUG),
    ),
  ),
  labtestsPrice: roundPrice(
    getItemPrices(
      invoiceItems?.filter((item) => item.type === ItemType.LAB && !item.isOptional).length > 0
        ? invoiceItems?.filter((item) => item.type === ItemType.LAB && !item.isOptional)
        : invoiceItems?.filter((item) => item.type === ItemType.LAB),
    ),
  ),
  counsellingsPrice: roundPrice(
    getItemPrices(
      invoiceItems?.filter(
        (item) => item.type === ItemType.CONSULTATION && item.name === CONSULTATION_NAMES.TH && !item.isOptional,
      ).length > 0
        ? invoiceItems?.filter(
            (item) => item.type === ItemType.CONSULTATION && item.name === CONSULTATION_NAMES.TH && !item.isOptional,
          )
        : invoiceItems?.filter((item) => item.type === ItemType.CONSULTATION && item.name === CONSULTATION_NAMES.TH),
    ),
  ),
  followupPrice: roundPrice(
    getItemPrices(
      invoiceItems?.filter((item) => item.type === ItemType.CONSULTATION && item.name === CONSULTATION_NAMES.FU),
    ),
  ),
});

export const getInvoiceItemsByCategory = (invoiceItems: IInvoiceItem[]) => ({
  drugs: invoiceItems?.filter((item) => item.type === ItemType.DRUG) || [],
  labtests: invoiceItems?.filter((item) => item.type === ItemType.LAB) || [],
  counsellings:
    invoiceItems?.filter((item) => item.type === ItemType.CONSULTATION && item.name === CONSULTATION_NAMES.TH) || [],
  followups:
    invoiceItems?.filter((item) => item.type === ItemType.CONSULTATION && item.name === CONSULTATION_NAMES.FU) || [],
});

export const getInvoiceItemsStatus = (paidItems: IInvoiceItem[], unpaidItems: IInvoiceItem[]) => {
  const {
    drugs: paidDrugs,
    labtests: paidLabtests,
    counsellings: paidCounsellings,
    followups: paidFollowups,
  } = getInvoiceItemsByCategory(paidItems || []);
  const {
    drugs: unpaidDrugs,
    labtests: unpaidLabtests,
    counsellings: unpaidCounsellings,
    followups: unpaidFollowups,
  } = getInvoiceItemsByCategory(unpaidItems || []);

  return {
    drugsStatus:
      paidDrugs.length > 0 && unpaidDrugs.length === 0
        ? BillType.PAID
        : paidDrugs.length > 0 && unpaidDrugs.length > 0
        ? BillType.PARTIAL
        : BillType.PREVIEW,
    labtestsStatus:
      paidLabtests.length > 0 && unpaidLabtests.length === 0
        ? BillType.PAID
        : paidLabtests.length > 0 && unpaidLabtests.length > 0
        ? BillType.PARTIAL
        : BillType.PREVIEW,
    counsellingsStatus:
      paidCounsellings.length > 0 && unpaidCounsellings.length === 0
        ? BillType.PAID
        : paidCounsellings.length > 0 && unpaidCounsellings.length > 0
        ? BillType.PARTIAL
        : BillType.PREVIEW,
    followupsStatus:
      paidFollowups.length > 0 && unpaidFollowups.length === 0
        ? BillType.PAID
        : paidFollowups.length > 0 && unpaidFollowups.length > 0
        ? BillType.PARTIAL
        : BillType.PREVIEW,
  };
};

export const formatInvoice = (invoice): IConsultationInvoice => {
  if (!invoice?.items?.length && !invoice?.paidItems?.length) return;
  const nonEmptyItems = invoice?.items?.filter((item) => item?.quantity > 0) || [];
  const items = nonEmptyItems?.map((item: any) => {
    return {
      ...item,
      costAmount: formatPrice(item.costAmount),
      originalAmount: formatPrice(item.originalAmount),
      listingAmount: formatPrice(item.listingAmount),
      payableAmount: formatPrice(item.payableAmount),
      additionalDiscount: formatPrice(item.originalAmount - item.payableAmount),
    };
  });

  const paidItems = invoice?.paidItems?.map((item: any) => {
    return {
      ...item,
      costAmount: formatPrice(item.costAmount),
      originalAmount: formatPrice(item.originalAmount),
      listingAmount: formatPrice(item.listingAmount),
      payableAmount: formatPrice(item.payableAmount),
      additionalDiscount: formatPrice(item.originalAmount - item.payableAmount),
    };
  });

  const listingAmount: number = items.reduce((acc: number, item: any) => acc + item.listingAmount, 0);
  const amount = formatPrice(invoice?.amount);
  const dueAmount = formatPrice(invoice?.dueAmount);
  const payableAmount = formatPrice(invoice?.payableAmount);
  const listingDiscount = Number.parseFloat((listingAmount - payableAmount).toFixed(2));
  const isValidCoupon = amount > payableAmount;
  const additionalDiscount = isValidCoupon ? Number.parseFloat((amount - payableAmount).toFixed(2)) : 0;
  return {
    ...invoice,
    items,
    paidItems,
    amount,
    payableAmount,
    dueAmount,
    listingDiscount,
    additionalDiscount,
    listingAmount,
    isValidCoupon,
  };
};

const groomInvoiceItem = (item, status: BillType): IInvoiceItem => {
  //this can be handled in backend, total price is incorrect in case of preference change
  const unitPayableAmount = item.payableAmount / item.quantity;
  const unitListingAmount = item.listingAmount / item.quantity;
  const unitOriginalAmount = item.originalAmount / item.quantity;
  const quantity =
    status === BillType.PAID
      ? item.quantity
      : item.purchasedQuantity !== item.modifiedQuantity
      ? item.modifiedQuantity || item.rxQuantity - item.purchasedQuantity
      : item.rxQuantity - item.purchasedQuantity;
  return {
    typeId: item.typeId,
    type: item.type,
    name: item.name,
    packSize: item.packSize,
    quantity: quantity,
    originalQuantity: item.rxQuantity,
    originalAmount: formatPrice(unitOriginalAmount * quantity),
    listingAmount: formatPrice(unitListingAmount * quantity),
    payableAmount: formatPrice(unitPayableAmount * quantity),
    additionalDiscount:
      item.name === CONSULTATION_NAMES.FU
        ? formatPrice(0)
        : formatPrice((unitOriginalAmount - unitPayableAmount) * quantity),
    isModified: item?.modifiedQuantity > 0 && item?.modifiedQuantity !== item?.quantity,
    isOptional: item?.isOptional,
    venues: item?.venues,
    availablePrices: Object.entries(item?.prices || {}).map(([locationId, price]: [string, IPrice]) => ({
      locationId,
      payableAmount: price?.sellingPrice,
      listingAmount: price?.listingPrice,
    })),
  };
};

export const getPreviewFromOverview = (overview: any, encounterId: string): IConsultationInvoice => {
  const items: IInvoiceItem[] = [];
  const paidItems: IInvoiceItem[] = [];
  const unserviceableItems: IInvoiceItem[] = [];

  for (const item of overview.paidItems) {
    paidItems.push(groomInvoiceItem(item, BillType.PAID));
  }
  for (const item of overview.unServiceableItems) {
    unserviceableItems.push(groomInvoiceItem(item, BillType.PREVIEW));
  }

  const itemsToProcess = overview.unpaidItems?.length > 0 ? overview.unpaidItems : overview.billingItems;

  for (const item of itemsToProcess) {
    //TODO: remove this check once backend is fixed
    if (item.quantity > 0)
      items.push(groomInvoiceItem(item, overview.unpaidItems?.length > 0 ? BillType.PARTIAL : BillType.PREVIEW));
  }

  return {
    encounterId,
    isValidCoupon: !!overview?.voucherCode,
    voucherCode: overview?.voucherCode,
    items,
    paidItems,
    unserviceableItems,
  };
};

export const formatPrice = (price): number => {
  return Number.parseFloat((price / 100).toFixed(2));
};

export const getSelectiveItemPricesFuture = (invoiceItems: IInvoiceItem[]) => {
  if (!invoiceItems?.length)
    return {
      listingAmount: 0,
      payableAmount: 0,
      listingDiscount: 0,
      additonalDiscount: 0,
    };
  const prices = invoiceItems.reduce(
    (acc, item) => {
      if (item?.selection?.isSelected) {
        acc.listingAmount += (item.listingAmount / item.quantity) * item?.selection?.quantity;
        acc.payableAmount += (item.payableAmount / item.quantity) * item?.selection?.quantity;
        acc.additionalDiscount +=
          ((item.originalAmount - item.payableAmount) / item.quantity) * item?.selection?.quantity;
      }
      return acc;
    },
    { listingAmount: 0, payableAmount: 0, additionalDiscount: 0 },
  );
  return {
    listingAmount: Number.parseFloat(prices.listingAmount.toFixed(2)),
    listingDiscount: Number.parseFloat(
      (prices.listingAmount - prices.payableAmount - prices.additionalDiscount).toFixed(2),
    ),
    additionalDiscount: Number.parseFloat(prices.additionalDiscount.toFixed(2)),
    payableAmount: Number.parseFloat(prices.payableAmount.toFixed(2)),
  };
};

export const getCounsellingCouponValidOrNot = (rx: IRx, isCounsellingSelected: boolean, payableAmount: number) => {
  const hasPrematureEjaculation = rx.diagnoses.some((diagnosis) =>
    diagnosis.name?.toLowerCase()?.includes('premature ejaculation'),
  );
  const hasErectileDysfunction = rx.diagnoses.some((diagnosis) =>
    diagnosis.name?.toLowerCase()?.includes('erectile dysfunction'),
  );
  if (
    rx.counsellings.length === 0 ||
    !isCounsellingSelected ||
    rx.appointmentType !== CONSULTATION_TYPE.SC ||
    // location id is not online or practo online
    ![ONLINE_LOCATION_ID, PRACTO_LOCATION_ID].includes(rx.locationId) ||
    rx.billType === BillType.MODIFIED
  ) {
    return false;
  }
  if (
    (hasPrematureEjaculation && !hasErectileDysfunction && payableAmount > 0) ||
    (hasErectileDysfunction && !hasPrematureEjaculation && payableAmount > 3000) ||
    (hasPrematureEjaculation && hasErectileDysfunction && payableAmount > 3000)
  ) {
    return true;
  }
  return false;
};
